import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router";
import { BackButton } from "@/components/common/BackButton";
import {
  POWER_GROUPS,
  POWER_LIBRARY,
  powerName,
} from "@/lib/power-library";
import type { PowerTag } from "@/lib/power-library";
import { POWERS_BOOKS, POWERS_ORIGINS } from "@/lib/powers-origins";

const CLUSTER_COPY: Record<PowerTag, { label: { id: string; defaultMessage: string }; description: { id: string; defaultMessage: string } }> = {
  Re: { label: { id: "power.tag.Re", defaultMessage: "Resilience" }, description: { id: "grow.about.cluster.Re", defaultMessage: "Resilience describes how people maintain focus, adapt to change, and respond to pressure or setbacks." } },
  Ro: { label: { id: "power.tag.Ro", defaultMessage: "Result Orientation" }, description: { id: "grow.about.cluster.Ro", defaultMessage: "Result Orientation describes how people set goals, plan their work, take responsibility, and follow through to achieve results." } },
  Fo: { label: { id: "power.tag.Fo", defaultMessage: "Future Orientation" }, description: { id: "grow.about.cluster.Fo", defaultMessage: "Future Orientation describes how people imagine possibilities, think beyond current circumstances, and approach uncertain situations with courage and conviction." } },
  In: { label: { id: "power.tag.In", defaultMessage: "Inventiveness" }, description: { id: "grow.about.cluster.In", defaultMessage: "Inventiveness describes how people explore questions, generate ideas, and respond creatively and independently to unfamiliar situations." } },
  Ts: { label: { id: "power.tag.Ts", defaultMessage: "Teamability" }, description: { id: "grow.about.cluster.Ts", defaultMessage: "Teamability describes how people understand others, respect differences, and develop relationships built on trust, commitment, and fairness." } },
  Co: { label: { id: "power.tag.Co", defaultMessage: "Communication" }, description: { id: "grow.about.cluster.Co", defaultMessage: "Communication describes how people recognize their own feelings and others' signals, express themselves, and develop shared understanding." } },
};

export function AboutPowersPage() {
  const intl = useIntl();
  const author = intl.formatMessage(POWERS_ORIGINS.author);
  const authorName = intl.formatMessage(POWERS_ORIGINS.authorName);
  return (
    <main className="w-full max-w-[1000px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10">
      <BackButton />
      <h1 id="grow-about-title" className="smycles-page-title mt-6">
        <FormattedMessage id="grow.about.title" defaultMessage={"Powers and team chemistry"} />
      </h1>
      <p className="smycles-article-width smycles-article-prose mt-6"><FormattedMessage id="grow.about.intro" defaultMessage={"People bring different ways of thinking, acting, and relating to others into a team. Smycles explores these differences through 30 Powers, a framework for understanding individual working styles and how they interact. Rather than placing people into fixed personality types, the Powers provide a shared language for understanding team chemistry."} /></p>
      <p className="smycles-article-width smycles-article-prose mt-3"><FormattedMessage id="grow.about.workingStyles" defaultMessage={"For example, one person may prefer uninterrupted time for focused work, while another responds quickly to changing priorities. Recognizing these differences can help teammates communicate their needs, coordinate their work, and build stronger relationships."} /></p>

      <section className="mt-10">
        <h2 id="grow-about-structure" className="text-smycles-title font-medium">
          <FormattedMessage id="grow.about.structureTitle" defaultMessage={"30 Powers, six clusters"} />
        </h2>
        <p className="smycles-article-width smycles-article-prose mt-3">
          <FormattedMessage id="grow.about.structure" defaultMessage={"The Smycles framework organizes 30 Powers into six clusters, each containing five Powers. These clusters describe different aspects of how people respond to challenges, pursue goals, explore ideas, and collaborate."} />
        </p>
        <div className="mt-6 grid gap-x-8 gap-y-8 @md:grid-cols-2">
          {POWER_GROUPS.map((tag) => (
            <section key={tag} id={`grow-about-cluster-${tag}`} aria-label={intl.formatMessage(CLUSTER_COPY[tag].label)}>
              <h3 id={`grow-about-cluster-${tag}-name`} className="text-smycles-body font-medium">
                <FormattedMessage {...CLUSTER_COPY[tag].label} />
              </h3>
              <p className="smycles-article-prose mt-2">
                <FormattedMessage {...CLUSTER_COPY[tag].description} />
              </p>
              <ul className="mt-3 flex flex-wrap gap-x-3 gap-y-2 text-smycles-meta text-ink-muted">
                {POWER_LIBRARY.filter((power) => power.tag === tag).map((power) => (
                  <li key={power.abbr}>
                    <Link
                      id={`grow-about-power-${power.abbr}`}
                      to={`/grow/power/${power.abbr}`}
                      className="underline underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4"
                    >
                      {powerName(intl, power.abbr)}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </section>

      <section className="smycles-article-width mt-10">
        <h2 id="grow-about-approach" className="text-smycles-title font-medium">
          <FormattedMessage id="grow.about.approachTitle" defaultMessage={"Research and development"} />
        </h2>
        <p className="smycles-article-prose mt-3">
          <FormattedMessage id="grow.about.approach" defaultMessage={"The Smycles framework draws on research into personality and human behavior, including established models such as the Big Five and HEXACO, as well as widely used personality assessments such as MBTI and DISC. While these approaches provide reference points, the 30 Powers and their organization into six clusters are specific to Smycles. The framework considers how individual tendencies interact with circumstances, experiences, and learned behaviors."} />
        </p>
        <p className="smycles-article-prose mt-3">
          <FormattedMessage id="grow.about.development" defaultMessage={"Research on attention, empathy, and improvisation informs the development of Power definitions and assessment questions. Smycles also brings together perspectives from psychology, biology, neuroscience, philosophy, and practical experience to explore human creativity and collaboration. Further studies are planned to examine the assessment's reliability and its potential value for understanding teamwork."} />
        </p>
      </section>
      <section className="smycles-article-width mt-10">
        <h2 id="grow-about-origins" className="text-smycles-title font-medium">
          <FormattedMessage id="grow.about.originsTitle" defaultMessage={"Origins of the framework"} />
        </h2>
        <p className="smycles-article-prose mt-3">
          <FormattedMessage id="grow.about.origins" defaultMessage={"The framework builds on {author}'s work on creativity and team composition. Smycles continues to develop these ideas into Power definitions, assessment questions, and a framework for understanding collaboration."} values={{ author }} />
        </p>
        <p className="smycles-article-prose mt-3">
          <FormattedMessage id="grow.about.publications" defaultMessage={"{authorName}'s publications and contributions include:"} values={{ authorName }} />
        </p>
        <ul className="mt-3 list-disc space-y-2 pl-5 text-smycles-body leading-relaxed text-ink-muted">
          {POWERS_BOOKS.map((book) => (
            <li key={book.title.id}>
              <cite><FormattedMessage {...book.title} /></cite>
              {book.credit ? (
                <span className="ml-2 whitespace-nowrap text-smycles-meta text-ink-muted">
                  (<FormattedMessage {...book.credit} />)
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      </section>
    </main>
  );
}
